"use client"

import React, { useState, useRef, useEffect, useCallback, useImperativeHandle } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Play, Pause, Upload, Power } from "lucide-react"


const NUM_PARTICLES = 1500
const PARTICLE_BASE_RADIUS = 1.2
const PARTICLE_MAX_ADDITIONAL_RADIUS = 25.0
const CORE_BASE_RADIUS = 15
const CORE_MAX_ADDITIONAL_RADIUS = 100
const ORBIT_BASE_RADIUS = 90
const ORBIT_MAX_EXPANSION = 700
const PERSPECTIVE_FOCAL_LENGTH = 280
const IDLE_PULSE_SPEED = 0.005
const IDLE_ORBIT_SPEED_FACTOR = 0.1
const AUDIO_ORBIT_SPEED_FACTOR = 5.0

const CANVAS_TRAIL_ALPHA = 0.15;
const CORE_COLORS_FUTURISTIC = [
  { stop: 0, color: "rgba(30, 30, 30, 1.0)" },
  { stop: 0.2, color: "rgba(50, 50, 50, 0.95)" },
  { stop: 0.6, color: "rgba(70, 70, 70, 0.7)" },
  { stop: 1, color: "rgba(90, 90, 90, 0.2)" },
]
const PARTICLE_COLORS_MONOCHROME = [
  "rgba(60, 60, 60, 1.0)",    // Dark gray
  "rgba(70, 70, 70, 0.95)",   // Slightly lighter dark gray
  "rgba(80, 80, 80, 0.9)",    // Medium-dark gray
]

const CONNECTION_LINE_WIDTH = 0.3
// MODIFIED: Increased audio interaction for line width
const CONNECTION_LINE_WIDTH_AUDIO_FACTOR = 2.0; 
const MAX_CONNECTION_DISTANCE_2D = 90
const MAX_NEIGHBORS_TO_CHECK_FOR_CONNECTION = 6
const CONNECTION_BASE_OPACITY = 0.008
// MODIFIED: Increased audio interaction for opacity
const CONNECTION_AUDIO_OPACITY_FACTOR = 10.0; 
const PARTICLE_GLOW_ALPHA = 0.3;
const PARTICLE_TRAIL_ALPHA = 0.8;
const CONNECTION_GLOW_ALPHA = 0.2;

const PARTICLE_Z_OSCILLATION_AMPLITUDE_BASE = 5;
const PARTICLE_Z_OSCILLATION_AMPLITUDE_AUDIO_FACTOR = 80;
const PARTICLE_Z_OSCILLATION_SPEED_MIN = 0.01;
const PARTICLE_Z_OSCILLATION_SPEED_MAX = 0.03;
const PARTICLE_Z_OSCILLATION_SPEED_AUDIO_FACTOR = 0.3;

const PARTICLE_ELEVATION_OSCILLATION_AMPLITUDE = 0.2;
const PARTICLE_ELEVATION_OSCILLATION_SPEED_MIN = 0.005;
const PARTICLE_ELEVATION_OSCILLATION_SPEED_MAX = 0.015;

const PARTICLE_RADIUS_OSCILLATION_AMPLITUDE = 30;
const PARTICLE_RADIUS_OSCILLATION_SPEED_MIN = 0.003;
const PARTICLE_RADIUS_OSCILLATION_SPEED_MAX = 0.01;

const PARTICLE_ANGULAR_WOBBLE_MAGNITUDE = 0.005;
const OSCILLATION_PHASE_RANDOM_DELTA = 0.001;

const CORE_GLOW_COLOR_HSL_MONOCHROME = { h: 0, s: 0, l: 10 };
// MODIFIED: Slightly lighter connection color
const CONNECTION_COLOR_HSL_MONOCHROME = { h: 0, s: 0, l: 45 }; 
const TRAIL_COLOR_HSL_MONOCHROME = { h: 0, s: 0, l: 0 }; // Black base for trails

const SYSTEM_ROTATION_SPEED = 0.0003;

const GLOBAL_PULSE_BASE_RADIUS = 0;
const GLOBAL_PULSE_MAX_RADIUS_AUDIO_FACTOR = 1500;
const GLOBAL_PULSE_MAX_ALPHA = 0.4;
const AUDIO_SMOOTHING_FACTOR = 0.1;

const INITIAL_PARTICLE_Z_OFFSET = 150;

// Helper function to convert HSL to RGBA for canvas
function hslToRgba(h: number, s: number, l: number, a: number): string {
    s /= 100;
    l /= 100;
    let c = (1 - Math.abs(2 * l - 1)) * s,
        x = c * (1 - Math.abs(((h / 60) % 2) - 1)),
        m = l - c / 2,
        r = 0,
        g = 0,
        b = 0;

    if (0 <= h && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (60 <= h && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (120 <= h && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (180 <= h && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (240 <= h && h < 300) {
        r = x;
        g = 0;
        b = c;
    } else if (300 <= h && h < 360) {
        r = c;
        g = 0;
        b = x;
    }
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

interface Particle {
  id: number
  x: number
  y: number
  z: number
  angle: number
  initialOrbitRadius: number
  initialElevationAngle: number
  speed: number
  color: string
  baseSize: number
  zOffsetAngle: number;
  zOscillationSpeed: number;
  prevX: number;
  prevY: number;
  prevZ: number;
  elevationOscillationPhase: number;
  elevationOscillationSpeed: number;
  radiusOscillationPhase: number;
  radiusOscillationSpeed: number;
}

interface ProjectedParticle extends Particle {
  x2d: number
  y2d: number
  scale: number;
  prevX2d: number;
  prevY2d: number;
}

// Define the interface for the functions exposed via ref
interface IOrbitalDataCloudRef {
  playAudio: () => void;
  pauseAudio: () => void;
}

// Define the component props interface
interface OrbitalDataCloudProps {
  className?: string;
  style?: React.CSSProperties;
  initialAudioUrl?: string;
  hideControls?: boolean; // NEW PROP: to hide the control buttons
}

// Wrap the component with React.forwardRef
const OrbitalDataCloud = React.forwardRef<IOrbitalDataCloudRef, OrbitalDataCloudProps>((props: OrbitalDataCloudProps, ref: React.Ref<IOrbitalDataCloudRef>) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  // `audioSrc` will now store the BLOB URL, which is same-origin
  const [audioSrc, setAudioSrc] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)
  // isSystemActive is now true by default. If hideControls is true, it remains true.
  // If controls are visible, it can be toggled.
  const [isSystemActive, setIsSystemActive] = useState(true) 
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioLoadError, setAudioLoadError] = useState<string | null>(null);

  const particlesRef = useRef<Particle[]>([])
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserNodeRef = useRef<AnalyserNode | null>(null)
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null)
  const animationFrameIdRef = useRef<number | null>(null)
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  const timeRef = useRef(0)
  const smoothedAudioVolumeRef = useRef(0);

  const initializeParticles = useCallback(() => {
    const newParticles: Particle[] = []
    for (let i = 0; i < NUM_PARTICLES; i++) {
      const angle = Math.random() * Math.PI * 2
      const initialOrbitRadius = ORBIT_BASE_RADIUS * (0.4 + Math.random() * 0.9)
      const initialElevationAngle = (Math.random() - 0.5) * Math.PI * 0.7;
      const speed = (0.3 + Math.random() * 0.7) * 0.006

      const initialX = initialOrbitRadius * Math.cos(angle) * Math.cos(initialElevationAngle);
      const initialY = initialOrbitRadius * Math.sin(angle) * Math.cos(initialElevationAngle);
      const initialZ = initialOrbitRadius * Math.sin(initialElevationAngle);

      newParticles.push({
        id: i,
        x: initialX,
        y: initialY,
        z: initialZ,
        prevX: initialX,
        prevY: initialY,
        prevZ: initialZ,
        angle,
        initialOrbitRadius,
        initialElevationAngle,
        speed,
        color: PARTICLE_COLORS_MONOCHROME[Math.floor(Math.random() * PARTICLE_COLORS_MONOCHROME.length)],
        baseSize: PARTICLE_BASE_RADIUS * (0.6 + Math.random() * 0.7),
        zOffsetAngle: Math.random() * Math.PI * 2,
        zOscillationSpeed: PARTICLE_Z_OSCILLATION_SPEED_MIN + Math.random() * (PARTICLE_Z_OSCILLATION_SPEED_MAX - PARTICLE_Z_OSCILLATION_SPEED_MIN),
        elevationOscillationPhase: Math.random() * Math.PI * 2,
        elevationOscillationSpeed: PARTICLE_ELEVATION_OSCILLATION_SPEED_MIN + Math.random() * (PARTICLE_ELEVATION_OSCILLATION_SPEED_MAX - PARTICLE_ELEVATION_OSCILLATION_SPEED_MIN),
        radiusOscillationPhase: Math.random() * Math.PI * 2,
        radiusOscillationSpeed: PARTICLE_RADIUS_OSCILLATION_SPEED_MIN + Math.random() * (PARTICLE_RADIUS_OSCILLATION_SPEED_MAX - PARTICLE_RADIUS_OSCILLATION_SPEED_MIN),
      })
    }
    particlesRef.current = newParticles
  }, [])

  useEffect(() => {
    initializeParticles()
  }, [initializeParticles])

  // EFFECT: To fetch the `initialAudioUrl` and convert it to a blob URL
  useEffect(() => {
    const fetchAndSetDefaultAudio = async () => {
      // Clean up previous blob URL if it exists
      if (audioSrc && audioSrc.startsWith("blob:")) {
        URL.revokeObjectURL(audioSrc);
      }

      if (props.initialAudioUrl) {
        setIsLoadingAudio(true);
        setAudioLoadError(null);
        setIsPlaying(false); // Stop any current playback

        try {
          console.log(`Attempting to fetch audio from: ${props.initialAudioUrl}`);
          const response = await fetch(props.initialAudioUrl);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} for ${props.initialAudioUrl}`);
          }
          const audioBlob = await response.blob();
          const blobUrl = URL.createObjectURL(audioBlob);
          
          setAudioSrc(blobUrl);
          // Extract filename from URL or use a generic name
          const urlParts = props.initialAudioUrl.split('/');
          setFileName(urlParts[urlParts.length - 1] || "dynamic-audio.wav");
          console.log("Audio loaded successfully as blob URL.");
        } catch (error) {
          console.error("Error fetching or processing default audio:", error);
          setAudioLoadError("Failed to load audio. Please check the URL and console.");
          setAudioSrc(null);
          setFileName(null);
        } finally {
          setIsLoadingAudio(false);
        }
      } else {
        // If initialAudioUrl is null/undefined, clear audio states
        setAudioSrc(null);
        setFileName(null);
        setIsPlaying(false);
        setIsLoadingAudio(false);
        setAudioLoadError(null);
      }
    };

    fetchAndSetDefaultAudio();

    // Cleanup when component unmounts or initialAudioUrl changes
    return () => {
      // This cleanup is crucial for blob URLs
      if (audioSrc && audioSrc.startsWith("blob:")) {
        URL.revokeObjectURL(audioSrc);
      }
    };
  }, [props.initialAudioUrl]);

  // This useEffect just connects the `audioSrc` state (which will now be a blob URL) to the HTML audio element.
  useEffect(() => {
    if (audioElementRef.current) {
      audioElementRef.current.src = audioSrc || ''; // Set to empty string if no audioSrc
    }
  }, [audioSrc]);


  const draw = useCallback(
    (currentAudioVolume = 0) => {
      const canvas = canvasRef.current
      // System is always considered active if controls are hidden, otherwise use state
      const effectiveIsSystemActive = props.hideControls || isSystemActive; 
      if (!canvas || !effectiveIsSystemActive) return // Use effectiveIsSystemActive here
      const ctx = canvas.getContext("2d")
      if (!ctx) return

      const width = canvas.width
      const height = canvas.height
      const centerX = width / 2
      const centerY = height / 2

      ctx.fillStyle = `rgba(255, 255, 255, ${CANVAS_TRAIL_ALPHA})`
      ctx.fillRect(0, 0, width, height)

      timeRef.current += IDLE_PULSE_SPEED
      const pulseFactor = (Math.sin(timeRef.current) + 1) / 2

      const systemRotationAngle = timeRef.current * SYSTEM_ROTATION_SPEED;

      // --- Draw Global Energy Pulse ---
      const pulseRadius = GLOBAL_PULSE_BASE_RADIUS + currentAudioVolume * GLOBAL_PULSE_MAX_RADIUS_AUDIO_FACTOR;
      const pulseAlpha = currentAudioVolume * GLOBAL_PULSE_MAX_ALPHA;
      if (pulseRadius > 0 && pulseAlpha > 0.005) {
          const pulseGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, pulseRadius);
          const currentPulseColor = CORE_GLOW_COLOR_HSL_MONOCHROME;
          pulseGradient.addColorStop(0, hslToRgba(currentPulseColor.h, currentPulseColor.s, currentPulseColor.l, pulseAlpha * 0.8));
          pulseGradient.addColorStop(0.5, hslToRgba(currentPulseColor.h, currentPulseColor.s, currentPulseColor.l, pulseAlpha * 0.4));
          pulseGradient.addColorStop(1, hslToRgba(currentPulseColor.h, currentPulseColor.s, currentPulseColor.l, 0));
          ctx.fillStyle = pulseGradient;
          ctx.beginPath();
          ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
          ctx.fill();
      }
      ctx.shadowBlur = 0;
      ctx.shadowColor = 'transparent';


      // --- Draw Core ---
      const coreRadius = CORE_BASE_RADIUS + pulseFactor * 3 + currentAudioVolume * CORE_MAX_ADDITIONAL_RADIUS
      const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreRadius)
      CORE_COLORS_FUTURISTIC.forEach((stopInfo) => {
        const baseAlpha = parseFloat(stopInfo.color.match(/[\d.]+\)$/)![0]);
        const dynamicAlpha = Math.min(1, baseAlpha + currentAudioVolume * 1.2); 
        coreGradient.addColorStop(
          stopInfo.stop,
          stopInfo.color.replace(/[\d.]+\)$/, `${dynamicAlpha})`),
        )
      })

      ctx.beginPath()
      ctx.arc(centerX, centerY, coreRadius, 0, Math.PI * 2)
      ctx.fillStyle = coreGradient
      const currentCoreGlowColor = CORE_GLOW_COLOR_HSL_MONOCHROME;
      ctx.shadowColor = hslToRgba(currentCoreGlowColor.h, currentCoreGlowColor.s, currentCoreGlowColor.l, 0.3 + currentAudioVolume * 5.0); 
      ctx.shadowBlur = 20 + currentAudioVolume * 120;
      ctx.fill()
      ctx.shadowBlur = 0

      // --- Update and Project Particles ---
      const currentOrbitExpansion = ORBIT_BASE_RADIUS + currentAudioVolume * ORBIT_MAX_EXPANSION
      const currentSpeedFactor = IDLE_ORBIT_SPEED_FACTOR + currentAudioVolume * AUDIO_ORBIT_SPEED_FACTOR
      const projectedParticles: ProjectedParticle[] = []

      particlesRef.current.forEach((p) => {
        p.prevX = p.x;
        p.prevY = p.y;
        p.prevZ = p.z;

        p.angle += p.speed * currentSpeedFactor;
        p.angle += (Math.random() - 0.5) * PARTICLE_ANGULAR_WOBBLE_MAGNITUDE;

        p.zOffsetAngle += p.zOscillationSpeed * (1 + currentAudioVolume * PARTICLE_Z_OSCILLATION_SPEED_AUDIO_FACTOR);
        p.zOffsetAngle += (Math.random() - 0.5) * OSCILLATION_PHASE_RANDOM_DELTA;

        p.elevationOscillationPhase += p.elevationOscillationSpeed;
        p.elevationOscillationPhase += (Math.random() - 0.5) * OSCILLATION_PHASE_RANDOM_DELTA;

        p.radiusOscillationPhase += p.radiusOscillationSpeed;
        p.radiusOscillationPhase += (Math.random() - 0.5) * OSCILLATION_PHASE_RANDOM_DELTA;

        const currentZOscillationAmplitude = PARTICLE_Z_OSCILLATION_AMPLITUDE_BASE + currentAudioVolume * PARTICLE_Z_OSCILLATION_AMPLITUDE_AUDIO_FACTOR;
        const currentElevationAngle = p.initialElevationAngle + Math.sin(p.elevationOscillationPhase) * PARTICLE_ELEVATION_OSCILLATION_AMPLITUDE;
        const currentParticleOrbitRadius = p.initialOrbitRadius + Math.sin(p.radiusOscillationPhase) * PARTICLE_RADIUS_OSCILLATION_AMPLITUDE;

        const r = currentParticleOrbitRadius + currentOrbitExpansion * 0.3;

        let x3d = r * Math.cos(p.angle) * Math.cos(currentElevationAngle);
        let y3d = r * Math.sin(p.angle) * Math.cos(currentElevationAngle);
        let z3d = r * Math.sin(currentElevationAngle) + Math.sin(p.zOffsetAngle) * currentZOscillationAmplitude;
        
        z3d += INITIAL_PARTICLE_Z_OFFSET;

        const rotatedX3d = x3d * Math.cos(systemRotationAngle) - z3d * Math.sin(systemRotationAngle);
        const rotatedZ3d = x3d * Math.sin(systemRotationAngle) + z3d * Math.cos(systemRotationAngle);
        
        p.x = rotatedX3d
        p.y = y3d
        p.z = rotatedZ3d

        const scale = PERSPECTIVE_FOCAL_LENGTH / (PERSPECTIVE_FOCAL_LENGTH + p.z)
        if (scale <= 0) {
            return; 
        }

        const x2d = centerX + p.x * scale
        const y2d = centerY + p.y * scale

        const prevScale = PERSPECTIVE_FOCAL_LENGTH / (PERSPECTIVE_FOCAL_LENGTH + p.prevZ);
        const prevX2d = centerX + p.prevX * prevScale;
        const prevY2d = centerY + p.prevY * prevScale;

        projectedParticles.push({ ...p, x2d, y2d, scale, prevX2d, prevY2d })
      })

      // --- Draw Connections ---
      const currentConnectionColor = CONNECTION_COLOR_HSL_MONOCHROME;
      const connectionGlowColor = hslToRgba(currentConnectionColor.h, currentConnectionColor.s, currentConnectionColor.l, CONNECTION_GLOW_ALPHA);
      const connectionLineColor = hslToRgba(currentConnectionColor.h, currentConnectionColor.s, currentConnectionColor.l, 1);
      const currentConnectionLineWidth = CONNECTION_LINE_WIDTH + currentAudioVolume * CONNECTION_LINE_WIDTH_AUDIO_FACTOR; 

      for (let i = 0; i < projectedParticles.length; i++) {
        const p1 = projectedParticles[i]
        for (
          let j = i + 1;
          j < Math.min(i + 1 + MAX_NEIGHBORS_TO_CHECK_FOR_CONNECTION, projectedParticles.length);
          j++
        ) {
          const p2 = projectedParticles[j]

          const dx = p1.x2d - p2.x2d
          const dy = p1.y2d - p2.y2d
          const dist = Math.sqrt(dx * dx + dy * dy)

          if (dist < MAX_CONNECTION_DISTANCE_2D) {
            const distanceFactor = 1 - (dist / MAX_CONNECTION_DISTANCE_2D);
            const opacity = (CONNECTION_BASE_OPACITY + currentAudioVolume * CONNECTION_AUDIO_OPACITY_FACTOR) * distanceFactor;
            
            if (opacity <= 0) continue

            ctx.beginPath()
            ctx.moveTo(p1.x2d, p1.y2d)
            ctx.lineTo(p2.x2d, p2.y2d)
            ctx.lineWidth = currentConnectionLineWidth * 3; 
            ctx.strokeStyle = connectionGlowColor.replace(/[\d.]+\)$/, `${Math.max(0, opacity * 0.5)})`);
            ctx.stroke()

            ctx.beginPath()
            ctx.moveTo(p1.x2d, p1.y2d)
            ctx.lineTo(p2.x2d, p2.y2d)
            ctx.lineWidth = currentConnectionLineWidth;
            ctx.strokeStyle = connectionLineColor.replace(/[\d.]+\)$/, `${Math.max(0, opacity)})`);
            ctx.stroke()
          }
        }
      }

      // --- Draw Particles ---
      projectedParticles.sort((a, b) => a.z - b.z)

      projectedParticles.forEach((pp) => {
        if (pp.x2d < 0 || pp.x2d > width || pp.y2d < 0 || pp.y2d > height) return

        const size = (pp.baseSize + currentAudioVolume * PARTICLE_MAX_ADDITIONAL_RADIUS) * pp.scale
        const baseAlpha = Number.parseFloat(pp.color.match(/[\d.]+\)$/)![0])
        const finalAlpha = Math.min(1, baseAlpha * (0.5 + pp.scale * 0.5) + currentAudioVolume * 0.3)

        // Draw particle trail
        if (Math.abs(pp.x2d - pp.prevX2d) > 0.1 || Math.abs(pp.y2d - pp.prevY2d) > 0.1) {
            ctx.beginPath();
            ctx.moveTo(pp.prevX2d, pp.prevY2d);
            ctx.lineTo(pp.x2d, pp.y2d);
            ctx.lineWidth = Math.max(0.1, size * 0.5);
            const currentTrailAlpha = Math.min(1, PARTICLE_TRAIL_ALPHA * (1 + currentAudioVolume * 2.0));
            ctx.strokeStyle = hslToRgba(TRAIL_COLOR_HSL_MONOCHROME.h, TRAIL_COLOR_HSL_MONOCHROME.s, TRAIL_COLOR_HSL_MONOCHROME.l, currentTrailAlpha);
            ctx.stroke();
        }

        // Draw particle glow/halo
        ctx.beginPath();
        ctx.arc(pp.x2d, pp.y2d, Math.max(0.1, size * 1.8), 0, Math.PI * 2);
        ctx.fillStyle = pp.color.replace(/[\d.]+\)$/, `${Math.min(0.5, finalAlpha * PARTICLE_GLOW_ALPHA * (1 + currentAudioVolume * 4.0))}`); 
        ctx.fill();

        // Draw actual particle
        ctx.beginPath();
        ctx.arc(pp.x2d, pp.y2d, Math.max(0.1, size), 0, Math.PI * 2);
        ctx.fillStyle = pp.color.replace(/[\d.]+\)$/, `${finalAlpha})`);
        ctx.fill();
      })
    },
    [isSystemActive, props.hideControls], // Include props.hideControls in dependency array
  )

  const animationLoop = useCallback(() => {
    let audioVolumeRaw = 0
    if (isPlaying && analyserNodeRef.current) {
      const dataArray = new Uint8Array(analyserNodeRef.current.frequencyBinCount) 
      analyserNodeRef.current.getByteFrequencyData(dataArray)
      let sum = 0
      for (let i = 0; i < dataArray.length; i++) sum += dataArray[i]
      audioVolumeRaw = dataArray.length > 0 ? sum / dataArray.length / 255 : 0
      audioVolumeRaw = Math.pow(audioVolumeRaw, 2.0); 
    }

    smoothedAudioVolumeRef.current = 
      smoothedAudioVolumeRef.current * (1 - AUDIO_SMOOTHING_FACTOR) + 
      audioVolumeRaw * AUDIO_SMOOTHING_FACTOR;

    draw(smoothedAudioVolumeRef.current);
    animationFrameIdRef.current = requestAnimationFrame(animationLoop)
  }, [isPlaying, draw])

  useEffect(() => {
    const canvas = canvasRef.current
    if (canvas) {
      const container = canvas.parentElement
      if (container) {
        canvas.width = container.clientWidth
        canvas.height = container.clientHeight
        initializeParticles()
      }
    }
    // Determine if system should be active based on prop or internal state
    const effectiveIsSystemActive = props.hideControls || isSystemActive; 

    if (effectiveIsSystemActive) { // Use effectiveIsSystemActive here
      if (!animationFrameIdRef.current) {
        animationFrameIdRef.current = requestAnimationFrame(animationLoop)
      }
    } else {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current)
        animationFrameIdRef.current = null
      }
      const ctx = canvas?.getContext("2d")
      if (ctx && canvas) {
        ctx.fillStyle = "white"; 
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current)
        animationFrameIdRef.current = null
      }
    }
  }, [animationLoop, isSystemActive, initializeParticles, props.hideControls]) // Add props.hideControls to deps

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // If a previous blob URL exists (from default or prior upload), revoke it
      if (audioSrc && audioSrc.startsWith("blob:")) {
        URL.revokeObjectURL(audioSrc);
      }
      const url = URL.createObjectURL(file)
      setAudioSrc(url)
      setFileName(file.name)
      setIsPlaying(false) // Reset playing state when new file loaded
      setAudioLoadError(null); // Clear any previous load error
    }
  }

  const setupAudioContext = useCallback(() => {
    if (!audioElementRef.current) {
        console.warn("Audio element not available for context setup.");
        return;
    }
    if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        console.log("AudioContext created.");
    }
    const audioContext = audioContextRef.current;

    // Create MediaElementSourceNode if it doesn't exist or if the media element changed
    if (!sourceNodeRef.current || sourceNodeRef.current.mediaElement !== audioElementRef.current) {
        if (sourceNodeRef.current) sourceNodeRef.current.disconnect(); // Disconnect old source if exists
        sourceNodeRef.current = audioContext.createMediaElementSource(audioElementRef.current);
        console.log("MediaElementAudioSourceNode created.");
    }

    // Create AnalyserNode if it doesn't exist
    if (!analyserNodeRef.current) {
        analyserNodeRef.current = audioContext.createAnalyser();
        analyserNodeRef.current.fftSize = 256;
        analyserNodeRef.current.smoothingTimeConstant = 0.5;
        console.log("AnalyserNode created.");
    }

    // Connect the nodes: source -> analyser -> destination
    // Disconnect existing connections to prevent multiple connections
    try {
        sourceNodeRef.current.disconnect();
        analyserNodeRef.current.disconnect();
    } catch (e) {
        /*ignore*/
    }
    sourceNodeRef.current.connect(analyserNodeRef.current);
    analyserNodeRef.current.connect(audioContext.destination);
    console.log("Audio nodes connected.");
  }, [])

  // Explicit play function
  const playAudio = useCallback(() => {
    // Only allow play if audio source exists, not already playing, not loading, and no error
    if (!audioSrc || isPlaying || isLoadingAudio || audioLoadError) return; 

    // This part handles resuming the AudioContext if it's suspended
    if (!audioContextRef.current) setupAudioContext(); // Ensure audio context is set up
    
    if (audioContextRef.current?.state === "suspended") {
      audioContextRef.current.resume().then(() => {
        console.log("AudioContext resumed.");
        audioElementRef.current?.play().catch(e => console.error("Error playing audio element after resume:", e));
        setIsPlaying(true);
      }).catch(e => console.error("Error resuming AudioContext:", e));
    } else {
      audioElementRef.current?.play().catch(e => console.error("Error playing audio element:", e));
      setIsPlaying(true);
    }
  }, [audioSrc, isPlaying, isLoadingAudio, audioLoadError, setupAudioContext]);

  // Explicit pause function
  const pauseAudio = useCallback(() => {
    if (!audioSrc || !isPlaying) return; 
    audioElementRef.current?.pause();
    setIsPlaying(false);
  }, [audioSrc, isPlaying]);

  // Internal toggle for the button (only used if controls are visible)
  const handleInternalTogglePlayPause = useCallback(() => {
    // Crucial: Call setupAudioContext here, within the user gesture handler.
    // This ensures AudioContext is created/setup only after the first click.
    if (!audioContextRef.current) {
      setupAudioContext();
    }

    if (isPlaying) {
      pauseAudio();
    } else {
      playAudio();
    }
  }, [isPlaying, playAudio, pauseAudio, setupAudioContext]);

  // IMPORTANT: The overall cleanup for blob URLs is now handled by the `useEffect` that fetches and creates them.
  // This general cleanup only needs to close the audio context.
  useEffect(() => {
    return () => {
      audioContextRef.current?.close().catch(console.error)
    }
  }, [])

  // Use useImperativeHandle to expose playAudio and pauseAudio
  useImperativeHandle(ref, () => ({
    playAudio,
    pauseAudio,
  }));

  const displayFileName = isLoadingAudio ? "Loading Audio..." : 
                          audioLoadError ? "Error Loading Audio" : 
                          fileName || "Upload Audio";

  return (
    <Card className="w-full max-w-2xl bg-transparent backdrop-blur-sm text-gray-800 shadow-none border-0">
      <CardContent className="p-0">
        <div className="w-full relative h-[500px]">
          <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full rounded-t-lg" />
          {/* Only show "SYSTEM OFFLINE" if controls are visible AND system is inactive */}
          {!isSystemActive && !props.hideControls && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-t-lg">
              <p className="text-2xl text-gray-500">SYSTEM OFFLINE</p>
            </div>
          )}
           {isLoadingAudio && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-lg rounded-t-lg">
              Loading Audio...
            </div>
          )}
          {audioLoadError && (
            <div className="absolute inset-0 flex items-center justify-center bg-red-800/70 text-white text-center p-4 rounded-t-lg">
              <p>{audioLoadError}</p>
            </div>
          )}
        </div>
      </CardContent>

      {/* Conditionally render the CardFooter based on hideControls prop */}
      {!props.hideControls && (
        <CardFooter className="flex flex-col sm:flex-row items-center gap-3 p-3 bg-transparent rounded-b-lg">
          <Button
            onClick={() => setIsSystemActive(!isSystemActive)}
            variant="ghost"
            className={`${isSystemActive ? "text-gray-700" : "text-gray-500"} transition-colors`}
            title={isSystemActive ? "Deactivate System" : "Activate System"}
          >
            <Power className="w-5 h-5" />
          </Button>
          <div className="flex-grow w-full sm:w-auto">
            <label
              htmlFor="audio-upload"
              className="flex items-center justify-center w-full px-3 py-2 rounded-md cursor-pointer hover:bg-gray-200/30 transition-colors text-sm text-gray-600 hover:text-gray-800"
            >
              <Upload className="w-4 h-4 mr-2" />
              <span>{displayFileName}</span>
              <Input id="audio-upload" type="file" accept="audio/*" onChange={handleFileChange} className="sr-only" />
            </label>
          </div>
          <Button
            onClick={handleInternalTogglePlayPause}
            // Disable if no audio, system inactive, or currently loading/errored
            disabled={!audioSrc || !isSystemActive || isLoadingAudio || !!audioLoadError}
            variant="ghost"
            className="text-gray-700 disabled:text-gray-400 disabled:hover:bg-transparent w-full sm:w-auto transition-colors"
          >
            {isPlaying ? <Pause className="w-5 h-5 mr-2" /> : <Play className="w-5 h-5 mr-2" />}
            {isPlaying ? "Pause" : "Play"}
          </Button>
        </CardFooter>
      )}
      <audio ref={audioElementRef} className="hidden" />
    </Card>
  );
});

export { OrbitalDataCloud };
export type { IOrbitalDataCloudRef as OrbitalDataCloudRef };